const pad = (n) => String(n).padStart(2, '0');

export const MONTHS = 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'.split(' ');

// An insight night runs noon to noon, named by its evening (CoreApi insight_night_of).
const NIGHT_START_HOUR = 12;

// Local date parts, never toISOString(): UTC conversion shifts the day for
// positive offsets (a live bug in the Flutter app we must not replicate).
export const formatLocalDate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// The furthest-ahead calendar date anywhere on Earth (UTC+14). A customer's night can carry
// tomorrow's date relative to the operator, so a browser-local "today" ceiling would hide it.
export const latestCalendarDate = (now = new Date()) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Pacific/Kiritimati',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);

// An IANA zone this browser knows, else null; Intl throws on an unknown name.
const knownZone = (zone) => {
  if (!zone) return null;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: zone });
    return zone;
  } catch {
    return null;
  }
};

const partsIn = (zone, now) =>
  Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: knownZone(zone) || 'UTC',
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
    })
      .formatToParts(now)
      .map(({ type, value }) => [type, Number(value)])
  );

// The customer's zone for a night: where it was recorded, then the profile zone, then UTC.
export const customerZone = (overview, night) =>
  knownZone(night?.timezone) || knownZone(overview?.timezone?.name) || 'UTC';

// A YYYY-MM-DD key moved by whole days, by calendar so no zone or DST can shift it.
export const shiftDateKey = (key, days) => {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
};

// The night holding `now` in `zone`: before local noon it is still last evening's night.
export const currentNightKey = (zone, now = new Date()) => {
  const { year, month, day, hour } = partsIn(zone, now);
  const key = `${year}-${pad(month)}-${pad(day)}`;
  return hour < NIGHT_START_HOUR ? shiftDateKey(key, -1) : key;
};

// Newest night a coach can open: the customer's current night, never below the latest recorded.
export const nightCap = (overview, now = new Date()) => {
  const zone = knownZone(overview?.timezone?.name);
  // No known zone: the furthest-ahead date, so no night is hidden.
  const current = zone ? currentNightKey(zone, now) : latestCalendarDate(now);
  const latest = overview?.latest_night;
  return latest && latest > current ? latest : current;
};

// AppDates.pageShowsYears: neither date in this year, or an older page once this Dec 31 night began.
const showsYears = (year, endYear, now) => {
  if (year !== now.year && endYear !== now.year) return true;
  return year < now.year && now.month === 12 && now.day === 31 && now.hour >= NIGHT_START_HOUR;
};

// "Sep 6 to 7", "Sep 30 to Oct 1": the app's page label (AppDates.pageRange) for a night key.
export const formatNightRange = (key, zone, now = new Date()) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key ?? '')) return '--';
  const [year, month, day] = key.split('-').map(Number);
  const [endYear, endMonth, endDay] = shiftDateKey(key, 1).split('-').map(Number);
  const start = `${MONTHS[month - 1]} ${day}`;
  const end = month === endMonth ? `${endDay}` : `${MONTHS[endMonth - 1]} ${endDay}`;
  if (!showsYears(year, endYear, partsIn(zone, now))) return `${start} to ${end}`;
  if (year === endYear) return `${start} to ${end}, ${endYear}`;
  return `${start}, ${year} to ${MONTHS[endMonth - 1]} ${endDay}, ${endYear}`;
};
